import { useState, useEffect } from 'react';
import { baserowAPI, Client } from '../services/baserowAPI';

// Cache global pour éviter les appels API répétés
let clientsCache: Client[] | null = null;
let clientsCachePromise: Promise<Client[]> | null = null;

// Listeners pour notifier les composants quand le cache est invalidé
let cacheInvalidationListeners: (() => void)[] = [];

// Fonction pour ajouter un listener
function addCacheInvalidationListener(callback: () => void) {
  cacheInvalidationListeners.push(callback);
  return () => {
    cacheInvalidationListeners = cacheInvalidationListeners.filter(cb => cb !== callback);
  };
}

// Fonction pour notifier tous les listeners
function notifyCacheInvalidation() {
  cacheInvalidationListeners.forEach(callback => callback());
}

// Fonction pour réinitialiser le cache
export function invalidateClientsCache() {
  clientsCache = null;
  clientsCachePromise = null;
  notifyCacheInvalidation();
}

export function useClients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoading(true);
        setError(null);

        // Si les clients sont déjà en cache, les utiliser
        if (clientsCache) {
          setClients(clientsCache);
          return;
        }

        // Si une requête est en cours, attendre son résultat
        if (clientsCachePromise) {
          const cachedClients = await clientsCachePromise;
          setClients(cachedClients);
          return;
        }

        // Sinon, faire la requête
        clientsCachePromise = baserowAPI.getAllClients();
        const loadedClients = await clientsCachePromise;
        
        // Mettre en cache et afficher
        clientsCache = loadedClients;
        setClients(loadedClients);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur lors du chargement des clients';
        setError(errorMessage);
        console.error('Erreur useClients:', err);
      } finally {
        setLoading(false);
      }
    };

    loadClients();
  }, [refreshTrigger]);

  // Écouter les invalidations du cache
  useEffect(() => {
    const unsubscribe = addCacheInvalidationListener(() => {
      setRefreshTrigger(prev => prev + 1);
    });
    return unsubscribe;
  }, []);

  return { clients, loading, error };
}
